import React, { useCallback, useState } from 'react';
import { View, Text, Pressable } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import InputSection from '@/components/General/inputs/InputSection';
import SheetSelector from '@/components/General/inputs/SheetSelector';
import ToggleInput from '@/components/General/inputs/ToggleInput';
import Unicon from '@/components/General/Unicon';

const FORM_PERMISSIONS = {
    'Submit Entries': 'canView',
    'Edit': 'canEdit',
    'Manage Entries': 'canManageEntries',
};

const PERMISSION_INFO = {
    'Submit Entries': 'Allows members in this role to see the published form and submit entries. They can fill it out but cannot edit the form or manage entries.',
    'Edit': 'Allows members in this role to manage the form — edit the schema, change settings, publish/close the form, and control which roles have access. Also allows reading entries and downloading their files, but not changing entries.',
    'Manage Entries': 'Allows members in this role to view, update, link, and delete this form\'s entries. Does not grant form editing.',
};

/**
 * Reusable picker for assigning roles to a form with per-role grant toggles.
 *
 * Props:
 * - value: array of { id, name, canView, canEdit, canManageEntries }
 * - onChange: callback with the updated array
 * - disabled: boolean (when !canEdit)
 * - fromScreen, fromStoreKey: passed through to SheetSelector for nested nav
 */
const FormRolesPicker = ({ value = [], onChange, disabled = false, isPublic = false, fromScreen = null, fromStoreKey = null }) => {
    const [showPermissionInfo, setShowPermissionInfo] = useState(false);

    // SheetSelector returns raw role objects from the query. Preserve grant
    // flags for roles already selected; default new ones to
    // canView=true ("Submit Entries"), canEdit=false, canManageEntries=false.
    const handleRolesChange = useCallback((selectedItems) => {
        const existingById = {};
        value.forEach((r) => { if (r.id) existingById[r.id] = r; });

        const merged = (selectedItems || []).map((r) => {
            const existing = existingById[r.id];
            if (existing) {
                // Public forms force canView=true
                return { ...existing, id: r.id, name: r.name, canView: isPublic ? true : existing.canView };
            }
            return { id: r.id, name: r.name, canView: true, canEdit: false, canManageEntries: false };
        });

        onChange(merged);
    }, [value, onChange, isPublic]);

    const handlePermissionChange = useCallback((roleId, permissionField, newValue) => {
        // Public forms: canView is always true, ignore attempts to turn it off
        if (isPublic && permissionField === 'canView') return;
        const updated = value.map((r) => {
            if (r.id !== roleId) return r;
            return { ...r, [permissionField]: newValue };
        });
        onChange(updated);
    }, [value, onChange, isPublic]);

    const handleSelectAll = useCallback((roleId, newValue) => {
        // Public forms: canView stays true, only toggle canEdit/canManageEntries
        const updated = value.map((r) => {
            if (r.id !== roleId) return r;
            return { ...r, canView: isPublic ? true : newValue, canEdit: newValue, canManageEntries: newValue };
        });
        onChange(updated);
    }, [value, onChange, isPublic]);

    const rolePermissionRows = value.map((role) => {
        const selectedNames = Object.entries(FORM_PERMISSIONS).map(([displayName, field]) => {
            // Public forms: Submit Entries is always on
            if (isPublic && field === 'canView') return displayName;
            return role[field] ? displayName : undefined;
        });

        // Public forms: Submit Entries is always on and can't be toggled off
        const lockedItems = isPublic ? ['Submit Entries'] : [];

        return (
            <ToggleInput
                key={role.id}
                title={role.name}
                enableSelectAll
                items={Object.keys(FORM_PERMISSIONS)}
                selectedValues={selectedNames}
                disabledItems={lockedItems}
                onItemChanged={(selectedItem, newValue) => {
                    handlePermissionChange(role.id, FORM_PERMISSIONS[selectedItem], newValue);
                }}
                onSelectAll={(_, newValue) => {
                    handleSelectAll(role.id, newValue);
                }}
                itemsPerRow={3}
                disabled={disabled}
            />
        );
    });

    return (
        <InputSection
            title="Roles"
            description={isPublic
                ? "Public forms are fillable by anyone with the link. Assign roles below to grant in-app edit or entry-management access. 'Submit Entries' is always on for public forms."
                : "Choose which roles can access this form. 'Submit Entries' lets people see and submit the form. 'Edit' lets people manage the form and its sharing. 'Manage Entries' lets people view and manage submissions."}
        >
            <View testID="form-roles-picker">
                <SheetSelector
                    title="Roles"
                    placeholder="Select roles that can access this form"
                    type="roles"
                    multiple
                    value={value}
                    onChange={handleRolesChange}
                    disabled={disabled}
                    fromScreen={fromScreen}
                    fromStoreKey={fromStoreKey}
                />

                {value.length > 0 && (
                    <View style={styles.permissionsContainer}>
                        <Pressable onPress={() => setShowPermissionInfo(true)} style={styles.permissionsHeader}>
                            <Text style={styles.permissionsTitle}>Permissions</Text>
                            <Unicon name="information-circle-outline" size={22} colorVariant="textSecondary" />
                        </Pressable>
                        <Text style={styles.permissionsDescription}>
                            Define what members of each role can do with this form. All roles get &apos;Submit Entries&apos; by default — add &apos;Edit&apos; for form management or &apos;Manage Entries&apos; for submission access.
                        </Text>
                    </View>
                )}

                {rolePermissionRows}

                {showPermissionInfo && (
                    <Pressable style={styles.infoOverlay} onPress={() => setShowPermissionInfo(false)}>
                        <View style={styles.infoModal}>
                            <View style={styles.infoCloseContainer}>
                                <Unicon name="close-outline" size={22} colorVariant="text" />
                            </View>
                            {Object.entries(PERMISSION_INFO).map(([name, description]) => (
                                <View key={name} style={styles.infoPermissionBlock}>
                                    <Text style={styles.infoPermissionTitle}>{name}</Text>
                                    <Text style={styles.infoPermissionDescription}>{description}</Text>
                                </View>
                            ))}
                        </View>
                    </Pressable>
                )}
            </View>
        </InputSection>
    );
};

const styles = StyleSheet.create(({ colors, fonts }, rt) => ({
    permissionsContainer: {
        marginTop: 20,
    },
    permissionsHeader: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    permissionsTitle: {
        fontSize: 18,
        color: colors.text,
        fontFamily: fonts.heading,
        paddingBottom: 5,
        marginRight: 5,
    },
    permissionsDescription: {
        fontStyle: 'italic',
        fontSize: 14,
        color: colors.textSecondary,
    },
    infoOverlay: {
        backgroundColor: 'rgba(0,0,0,0.5)',
        position: 'absolute',
        width: rt.screen.width,
        height: rt.screen.height,
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000,
    },
    infoModal: {
        width: 400,
        padding: 20,
        boxShadow: '0px 0px 15px -8px rgba(0, 0, 0, 0.2)',
        borderRadius: 8,
        backgroundColor: colors.background,
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: [{ translateX: '-50%' }, { translateY: '-50%' }],
    },
    infoCloseContainer: {
        position: 'absolute',
        right: 15,
        top: 15,
    },
    infoPermissionBlock: {
        borderBottomWidth: 1,
        borderBottomColor: colors.border,
        paddingVertical: 20,
    },
    infoPermissionTitle: {
        fontSize: 16,
        color: colors.text,
        fontFamily: fonts.bold,
        marginBottom: 10,
    },
    infoPermissionDescription: {
        fontSize: 14,
        color: colors.text,
        fontFamily: fonts.body,
    },
}));

export default FormRolesPicker;
